import { createFileRoute } from "@tanstack/react-router";
import { Info, Loader2, RotateCcw, Save } from "lucide-react";
import { Slider as SliderPrimitive, Switch as SwitchPrimitive } from "radix-ui";
import { useState } from "react";
import { useScopeTree } from "@/components/scope-picker/use-scope-tree";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
	useDeleteStrategConfigOverride,
	useStrategConfig,
	useUpdateStrategConfig,
} from "@/features/strateg-cenowy/hooks/use-strateg-config";
import type { StrategConfig, StrategyName } from "@/features/strateg-cenowy/types";
import { useStrategScope } from "@/features/strateg-cenowy/use-strateg-scope";
import { ensureElementVisible } from "@/lib/route-guards";
import { cn } from "@/lib/utils";

// ─── Strategy definitions ────────────────────────────────────────────────────

const STRATEGIES: {
	name: StrategyName;
	label: string;
	description: string;
	deciles: number[];
}[] = [
	{
		name: "avg-1-5",
		label: "Ekonomiczna",
		description: "Srednia Dec1-5: zrownowazona cena, 50% rynku",
		deciles: [1, 2, 3, 4, 5],
	},
	{
		name: "avg-1-2",
		label: "Agresywna",
		description: "Srednia Dec1-2: konkurencyjna cena, ponizej 20% rynku",
		deciles: [1, 2],
	},
	{
		name: "avg-3-5",
		label: "Podstawowa",
		description: "Srednia Dec3-5: wyzsza cena, gorna polowa rynku",
		deciles: [3, 4, 5],
	},
];

type ToggleKey = "roundingEnabled" | "premiumEnabled" | "seasonalEnabled" | "trendEnabled";

const FEATURE_TOGGLES: { key: ToggleKey; label: string; description: string }[] = [
	{
		key: "roundingEnabled",
		label: "Zaokraglanie psychologiczne",
		description: "Automatyczne zaokraglanie do cen konczacych sie na .99, .49 itp.",
	},
	{
		key: "premiumEnabled",
		label: "Premium brands",
		description: "Modyfikator cen dla marek premium (wyzsza marza)",
	},
	{
		key: "seasonalEnabled",
		label: "Sezonowe",
		description: "Korekty cen uwzgledniajace sezonowosc (grypa, alergie)",
	},
	{
		key: "trendEnabled",
		label: "Analiza trendow",
		description: "Uwzglednianie trendow cenowych z ostatnich 90 dni",
	},
];

// ─── Reusable Switch ─────────────────────────────────────────────────────────

function Toggle({
	checked,
	onCheckedChange,
	id,
}: {
	checked: boolean;
	onCheckedChange: (v: boolean) => void;
	id: string;
}) {
	return (
		<SwitchPrimitive.Root
			id={id}
			checked={checked}
			onCheckedChange={onCheckedChange}
			className={cn(
				"peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				"disabled:cursor-not-allowed disabled:opacity-50",
				"data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted-foreground/30",
			)}
		>
			<SwitchPrimitive.Thumb
				className={cn(
					"pointer-events-none block size-4 rounded-full bg-white shadow-lg ring-0 transition-transform",
					"data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
				)}
			/>
		</SwitchPrimitive.Root>
	);
}

// ─── Reusable Slider ─────────────────────────────────────────────────────────

function RangeSlider({
	value,
	onValueChange,
	min,
	max,
	step = 1,
	id,
}: {
	value: number;
	onValueChange: (v: number) => void;
	min: number;
	max: number;
	step?: number;
	id: string;
}) {
	return (
		<SliderPrimitive.Root
			id={id}
			value={[value]}
			onValueChange={(v) => onValueChange(v[0])}
			min={min}
			max={max}
			step={step}
			className="relative flex w-full touch-none select-none items-center"
		>
			<SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted-foreground/20">
				<SliderPrimitive.Range className="absolute h-full bg-primary" />
			</SliderPrimitive.Track>
			<SliderPrimitive.Thumb
				className={cn(
					"block size-4 rounded-full border-2 border-primary bg-white shadow transition-colors",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
				)}
			/>
		</SliderPrimitive.Root>
	);
}

// ─── Decile formula visualization ────────────────────────────────────────────

function DecileFormula({ activeDeciles }: { activeDeciles: number[] }) {
	return (
		<div className="flex items-center gap-1 mt-3 max-md:flex-wrap">
			{Array.from({ length: 10 }, (_, i) => i + 1).map((d) => (
				<div
					key={d}
					className={cn(
						"flex size-7 items-center justify-center rounded text-xs font-medium border transition-colors",
						activeDeciles.includes(d)
							? "bg-[var(--green-100)] text-[var(--green-700)] border-[var(--green-300)]"
							: "bg-muted/50 text-muted-foreground/50 border-transparent",
					)}
				>
					D{d}
				</div>
			))}
		</div>
	);
}

// ─── Manual strategy card (D1-D10 toggle squares) ───────────────────────────

function ManualStrategyCard({
	deciles,
	onTogglDecile,
}: {
	deciles: number[];
	onTogglDecile: (d: number) => void;
}) {
	const empty = deciles.length === 0;
	return (
		<Card>
			<CardHeader>
				<CardTitle>Manualna</CardTitle>
				<CardDescription>
					Wybierz dowolny zestaw decyli — strategia uzyje sredniej tylko z zaznaczonych. Aktywujesz
					ja przyciskiem "Manualna" w panelu Wyniki.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<div className="flex flex-wrap items-center gap-1.5">
					{Array.from({ length: 10 }, (_, i) => i + 1).map((d) => {
						const active = deciles.includes(d);
						return (
							<button
								key={d}
								type="button"
								onClick={() => onTogglDecile(d)}
								className={cn(
									"flex size-8 items-center justify-center rounded text-xs font-medium border transition-all cursor-pointer select-none max-md:size-9",
									active
										? "bg-[var(--green-100)] text-[var(--green-700)] border-[var(--green-400)] shadow-sm"
										: "bg-muted/30 text-muted-foreground border-muted-foreground/20 hover:border-muted-foreground/50",
								)}
								aria-pressed={active}
								aria-label={`Decyl ${d}`}
							>
								D{d}
							</button>
						);
					})}
				</div>
				{empty && <p className="text-xs text-destructive mt-2">Wybierz przynajmniej jeden decyl</p>}
			</CardContent>
		</Card>
	);
}

// ─── Local form overrides (null = unchanged from server) ─────────────────────

type FormOverrides = Partial<Omit<StrategConfig, "roundingRanges">>;

const ALL_DECILES: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function useConfigForm(config: StrategConfig | undefined) {
	const [overrides, setOverrides] = useState<FormOverrides>({});

	const effective = {
		defaultStrategy: overrides.defaultStrategy ?? config?.defaultStrategy ?? "avg-1-5",
		manualDeciles: overrides.manualDeciles ?? config?.manualDeciles ?? ALL_DECILES,
		anomalyThresholdFull: overrides.anomalyThresholdFull ?? config?.anomalyThresholdFull ?? 20,
		anomalyThresholdPartial:
			overrides.anomalyThresholdPartial ?? config?.anomalyThresholdPartial ?? 15,
		roundingEnabled: overrides.roundingEnabled ?? config?.roundingEnabled ?? true,
		premiumEnabled: overrides.premiumEnabled ?? config?.premiumEnabled ?? true,
		seasonalEnabled: overrides.seasonalEnabled ?? config?.seasonalEnabled ?? true,
		trendEnabled: overrides.trendEnabled ?? config?.trendEnabled ?? true,
		thinMarketVolume: overrides.thinMarketVolume ?? config?.thinMarketVolume ?? 10,
	};

	const isDirty = Object.keys(overrides).length > 0;

	function set<K extends keyof FormOverrides>(key: K, value: FormOverrides[K]) {
		setOverrides((prev) => ({ ...prev, [key]: value }));
	}

	function reset() {
		setOverrides({});
	}

	return { effective, isDirty, set, reset };
}

// ─── Page component ──────────────────────────────────────────────────────────

function scopeLabelByEffective(
	type: string | undefined,
	value: string | undefined,
	tree: ReturnType<typeof useScopeTree>["data"],
): string {
	if (!type) return "—";
	switch (type) {
		case "org":
			return "Cala organizacja";
		case "wlasne":
			return "Wlasne";
		case "franczyza":
			return "Franczyza";
		case "spolka": {
			const sp = tree?.spolki.find((x) => x.nip === value);
			return sp?.name ?? `Spolka NIP ${value ?? ""}`;
		}
		case "apteka": {
			const idfirm = Number(value);
			if (!Number.isFinite(idfirm)) return `Apteka ${value}`;
			if (tree) {
				for (const r of tree.wlasne.regions) {
					const p = r.pharmacies.find((x) => x.idfirm === idfirm);
					if (p) return p.name;
				}
				for (const sp of tree.spolki) {
					const p = sp.pharmacies.find((x) => x.idfirm === idfirm);
					if (p) return p.name;
				}
				for (const o of tree.orphanGroups) {
					const p = o.pharmacies.find((x) => x.idfirm === idfirm);
					if (p) return p.name;
				}
			}
			return `Apteka #${idfirm}`;
		}
		case "orphan":
			return value ? `Orphan ${value}` : "Orphan bez NIP";
		default:
			return type;
	}
}

function StrategiePage() {
	const { scope } = useStrategScope();
	const { data: config, isLoading, error } = useStrategConfig(scope);
	const { data: tree } = useScopeTree();
	const updateMutation = useUpdateStrategConfig(scope);
	const resetOverrideMutation = useDeleteStrategConfigOverride(scope);
	const { effective, isDirty, set, reset } = useConfigForm(config);

	function handleSave() {
		updateMutation.mutate(effective, { onSuccess: reset });
	}

	function handleResetToInherited() {
		if (
			// biome-ignore lint/suspicious/noAlert: Legacy dialog; replace with ConfirmDialog when changing this flow.
			window.confirm(
				"Usunac override na tym poziomie? Ustawienia powroca do dziedziczonych z poziomu wyzej.",
			)
		) {
			resetOverrideMutation.mutate(undefined, { onSuccess: reset });
		}
	}

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4 p-4">
				<PageHeader title="Strategie cenowe" />
				<div className="flex items-center justify-center py-16 text-muted-foreground">
					<Loader2 className="size-5 animate-spin mr-2" />
					Wczytywanie konfiguracji...
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col gap-4 p-4">
				<PageHeader title="Strategie cenowe" />
				<div className="rounded-lg border border-destructive/30 bg-destructive/5 p-8 text-center text-destructive">
					Blad wczytywania konfiguracji: {error.message}
				</div>
			</div>
		);
	}

	const isInherited = config?.isInherited === true;
	const effectiveLabel = scopeLabelByEffective(
		config?.effectiveScopeType,
		config?.effectiveScopeValue,
		tree,
	);
	const canResetOverride = !isInherited && scope.type !== "org" && scope.type !== "region";

	return (
		<div className="flex flex-col gap-4 md:gap-6 p-3 md:p-4">
			<PageHeader
				title="Strategie cenowe"
				description="Wybierz strategie i ustaw parametry ochrony cenowej"
			/>

			{/* ── Banner dziedziczenia / aktywnego override ──────────────── */}
			<div
				className={cn(
					"flex flex-wrap items-center gap-3 rounded-lg border px-3 md:px-4 py-3 text-sm",
					isInherited
						? "border-amber-300 bg-amber-50 text-amber-900"
						: "border-emerald-300 bg-emerald-50 text-emerald-900",
				)}
			>
				<Info className="size-4 shrink-0" />
				<div className="flex-1 min-w-[200px]">
					{isInherited ? (
						<>
							<span className="font-medium">Ustawienia dziedziczone z: {effectiveLabel}.</span>{" "}
							Zapisz zmiany aby utworzyc wlasny override dla wybranego scope.
						</>
					) : (
						<>
							<span className="font-medium">Aktywny override na poziomie: {effectiveLabel}.</span>{" "}
							Te ustawienia nadpisuja dziedziczone z wyzszych poziomow.
						</>
					)}
				</div>
				{canResetOverride && (
					<Button
						variant="outline"
						size="sm"
						className="max-md:h-9"
						onClick={handleResetToInherited}
						disabled={resetOverrideMutation.isPending}
					>
						{resetOverrideMutation.isPending ? (
							<Loader2 className="size-3.5 animate-spin" />
						) : (
							<RotateCcw className="size-3.5" />
						)}
						Resetuj do dziedziczonego
					</Button>
				)}
			</div>

			{/* ── Preset strategy cards (informacyjne) ─────────────────────── */}
			<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
				{STRATEGIES.map((s) => (
					<Card key={s.name}>
						<CardHeader>
							<CardTitle>{s.label}</CardTitle>
							<CardDescription>{s.description}</CardDescription>
						</CardHeader>
						<CardContent>
							<DecileFormula activeDeciles={s.deciles} />
						</CardContent>
					</Card>
				))}
			</div>

			{/* ── Manualna strategia ──────────────────────────────────────── */}
			<ManualStrategyCard
				deciles={effective.manualDeciles}
				onTogglDecile={(d) => {
					const next = effective.manualDeciles.includes(d)
						? effective.manualDeciles.filter((x) => x !== d)
						: [...effective.manualDeciles, d].sort((a, b) => a - b);
					set("manualDeciles", next);
				}}
			/>

			{/* ── Anomaly thresholds ─────────────────────────────────────── */}
			<Card>
				<CardHeader>
					<CardTitle>Progi ochrony anomalii</CardTitle>
					<CardDescription>
						Zabezpieczenie przed gwaltownymi spadkami cen i zbyt małym rynkiem lokalnym
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<div className="flex flex-col gap-3">
						<div className="flex items-center justify-between">
							<Label htmlFor="threshold-full">Prog pelnej ochrony</Label>
							<span className="text-sm font-medium tabular-nums">
								{effective.anomalyThresholdFull}%
							</span>
						</div>
						<RangeSlider
							id="threshold-full"
							value={effective.anomalyThresholdFull}
							onValueChange={(v) => set("anomalyThresholdFull", v)}
							min={15}
							max={30}
							step={1}
						/>
						<p className="text-xs text-muted-foreground">
							Pelna ochrona dla spadkow wiekszych niz {effective.anomalyThresholdFull}% — cena
							pozostaje niezmieniona
						</p>
					</div>

					<div className="flex flex-col gap-3">
						<div className="flex items-center justify-between">
							<Label htmlFor="threshold-partial">Prog czesciowej ochrony</Label>
							<span className="text-sm font-medium tabular-nums">
								{effective.anomalyThresholdPartial}%
							</span>
						</div>
						<RangeSlider
							id="threshold-partial"
							value={effective.anomalyThresholdPartial}
							onValueChange={(v) => set("anomalyThresholdPartial", v)}
							min={10}
							max={25}
							step={1}
						/>
						<p className="text-xs text-muted-foreground">
							Czesciowa ochrona (blend) dla spadkow wiekszych niz{" "}
							{effective.anomalyThresholdPartial}%
						</p>
					</div>

					<div className="flex flex-col gap-3">
						<div className="flex items-center justify-between">
							<Label htmlFor="thin-market-volume">Mały rynek lokalny</Label>
							<span className="text-sm font-medium tabular-nums">
								{effective.thinMarketVolume === 0
									? "wyłączone"
									: `< ${effective.thinMarketVolume} op.`}
							</span>
						</div>
						<RangeSlider
							id="thin-market-volume"
							value={effective.thinMarketVolume}
							onValueChange={(v) => set("thinMarketVolume", v)}
							min={0}
							max={50}
							step={1}
						/>
						<p className="text-xs text-muted-foreground">
							Gdy rynek lokalny IQVIA ma mniej opakowań, a jego decyle są poniżej ceny zakupu, ceny
							strategii liczone są z decyli sieci (mediana min. 5 aptek organizacji). 0 wyłącza.
						</p>
					</div>
				</CardContent>
			</Card>

			{/* ── Feature toggles ────────────────────────────────────────── */}
			<Card>
				<CardHeader>
					<CardTitle>Funkcje dodatkowe</CardTitle>
					<CardDescription>Wlacz lub wylacz moduly wplywajace na kalkulacje cen</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					{FEATURE_TOGGLES.map((toggle, i) => (
						<div key={toggle.key}>
							{i > 0 && <div className="h-px bg-border mb-4" />}
							<div className="flex items-center justify-between max-md:gap-3">
								<div>
									<Label htmlFor={`toggle-${toggle.key}`}>{toggle.label}</Label>
									<p className="text-xs text-muted-foreground mt-0.5">{toggle.description}</p>
								</div>
								<Toggle
									id={`toggle-${toggle.key}`}
									checked={effective[toggle.key]}
									onCheckedChange={(v) => set(toggle.key, v)}
								/>
							</div>
						</div>
					))}
				</CardContent>
			</Card>

			{/* ── Save button — sticky bottom ─────────────────────────────── */}
			<div className="sticky bottom-0 -mx-3 -mb-3 md:-mx-4 md:-mb-4 mt-2 flex flex-wrap justify-end gap-2 border-t border-border bg-background/95 px-3 md:px-4 py-3 backdrop-blur-sm">
				{isDirty && (
					<span className="self-center text-xs text-muted-foreground">Niezapisane zmiany</span>
				)}
				<Button
					onClick={handleSave}
					disabled={!isDirty || updateMutation.isPending || effective.manualDeciles.length === 0}
					className="min-w-[140px] max-md:h-11 max-md:flex-1"
				>
					{updateMutation.isPending ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						<Save className="size-4" />
					)}
					Zapisz zmiany
				</Button>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/_auth/strateg-cenowy/strategie")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.strateg-cenowy.strategie",
			location.pathname,
		);
	},
	component: StrategiePage,
});
